import { directApiInstance } from '../api/httpClient';

// Portal API base URL
const PORTAL_API_URL = `/portal`;

// Booking API base — the booking Lambda's own routes (functions/booking/lambda_function.py),
// used by BookingPaymentService below. Distinct from PORTAL_API_URL: the booking lambda's
// payment/setup|enroll|validate wrap the same CyberSource/Portal call as this file's Step 1/3/5,
// but front it with booking-specific fields (service_id -> merchant resolution) and their own
// snake_case response shape.
const BOOKING_API_URL = `/bookings`;

/**
 * Where Cardinal Commerce should POST after a step-up challenge completes, so `ThreeDSModal`'s
 * postMessage/same-origin detection fires. SHARED by the product flow's `checkEnrollment` below
 * and `BookingPaymentService.checkEnrollment` — the portalApi Lambda's early POST-body handler
 * (functions/portalApi/lambda_function.py's `lambda_handler`, ~line 1139) that turns this into a
 * `window.parent.postMessage({TransactionId})` page is completely generic: it just extracts
 * `TransactionId` from any form-urlencoded POST body, with no dependency on which flow (product
 * or booking) started the Cardinal session. Reusing the exact same return URL — rather than the
 * booking Lambda's own `/bookings/3ds-callback` (built for a MOBILE in-app-browser + DynamoDB
 * polling flow, `handle_3ds_callback` in functions/booking/lambda_function.py, and emits no
 * postMessage at all) — is what lets `ThreeDSModal` work unmodified for a booking-flow charge.
 */
export function resolveThreeDSReturnUrl(): string {
  const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  return isLocalhost
    ? `${window.location.origin}/3ds-complete.html`
    : `${import.meta.env.VITE_API_BASE_URL}/portal`;
}

export interface PaymentInfo {
  amount: number;
  currency: string;
  cardNumber: string;
  cardholderName: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
  email: string;
  phone: string;
  // Merchant-Defined Information (MDI) — required by VisaNet DR / Cybersource
  customerId?: string;        // MDI key 4: customer account ID or guest identifier
  merchantCategory?: string;  // MDI key 1: RETAIL | FAST FOOD | TELECOM | etc.
  channel?: string;           // MDI key 3: WEB | MOBILE
  idType?: string;            // MDI key 29: CEDULA | PASAPORTE
  idNumber?: string;          // MDI key 30: document number
  businessName?: string;       // Business name — sent as locality so cardholders see the origin
}

/** Step 1 response: Setup Payer Authentication */
export interface SetupPayerAuthResponse {
  status: string;
  step: string;
  deviceDataCollection?: {
    deviceDataCollectionUrl?: string;
    accessToken?: string;  // Cardinal token for device data collection
    referenceId?: string;
  };
  message?: string;
}

/** Step 3 response: Check Enrollment */
export interface CheckEnrollmentResponse {
  status: string;  // PENDING_AUTHENTICATION | AUTHENTICATION_SUCCESSFUL
  step?: string;
  authenticationTransactionId?: string; // normalized at root by backend
  challengeRequired?: string;           // 'Y' | 'N'
  consumerAuthenticationInformation?: {
    authenticationTransactionId?: string;
    accessToken?: string;  // Long JWT — used for step-up iframe POST
    token?: string;        // Short Cardinal session ref (NOT for step-up form)
    stepUpUrl?: string;
    pareq?: string;
    acsUrl?: string;
    paresStatus?: string;
    veresEnrolled?: string;
    specificationVersion?: string;
    challengeRequired?: string;
    directoryServerTransactionId?: string;
    acsTransactionId?: string;
    threeDSServerTransactionId?: string;
    // Frictionless (AUTHENTICATION_SUCCESSFUL) returns the auth result inline here
    cavv?: string;
    eciRaw?: string;
    eci?: string;
    xid?: string;
    indicator?: string;
    ecommerceIndicator?: string;
    strongAuthentication?: { OutageExemptionIndicator?: string };
  };
  stepUpAuthentication?: {
    stepUpUrl?: string;
    accessToken?: string;  // Long JWT — null when no real challenge
  } | null;
  authentication_info?: {
    consumerAuthenticationInformation?: Record<string, string>;
    status?: string;
  };
  enrolled?: boolean;
  message?: string;
}

/** Step 5 response: Validate Authentication Results */
export interface ValidateAuthenticationResponse {
  status: string;  // AUTHENTICATION_SUCCESSFUL | AUTHENTICATION_FAILED
  id?: string;
  consumerAuthenticationInformation?: {
    authenticationTransactionId?: string;
    cavv?: string;
    eciRaw?: string;
    eci?: string;
    paresStatus?: string;
    xid?: string;
    indicator?: string;            // 'vbv' | 'spa' | 'aesk'
    ucafCollectionIndicator?: string;
    ucafAuthenticationData?: string;
    directoryServerTransactionId?: string;
    paSpecificationVersion?: string;
    specificationVersion?: string;
    acsTransactionId?: string;
    strongAuthentication?: { OutageExemptionIndicator?: string };
    authorizationPayload?: string;
  };
}

/** Auth data from step 5 to be passed to the final payment */
export interface ThreeDSAuthData {
  cavv?: string;
  eciRaw?: string;
  eci?: string;
  paresStatus?: string;
  xid?: string;
  commerceIndicator?: string;
  ucafCollectionIndicator?: string;
  ucafAuthenticationData?: string;
  directoryServerTransactionId?: string;
  paSpecificationVersion?: string;
  acsTransactionId?: string;
  authenticationTransactionId?: string;
  strongAuthentication?: Record<string, string>;
}

/** Step 6 response: Final Payment */
export interface FinalPaymentResponse {
  status: string;
  id?: string;
  message?: string;
  authCode?: string;
  processorInformation?: {
    approvalCode?: string;
    responseCode?: string;
    networkTransactionId?: string;
  };
}

/**
 * Portal API Service for 3DS Payment Processing
 *
 * Official 6-step flow (per Cybersource docs):
 *   1. setupPayerAuth()    → POST /risk/v1/authentication-setups → deviceDataCollectionUrl + token
 *   2. collectDeviceData() → hidden iframe to Cardinal Commerce
 *   3. checkEnrollment()   → POST /risk/v1/authentications (WITH returnUrl!) → PENDING_AUTHENTICATION or AUTHENTICATION_SUCCESSFUL
 *      - AUTHENTICATION_SUCCESSFUL → skip to step 6
 *      - PENDING_AUTHENTICATION + accessToken → step 4
 *   4. ThreeDSModal        → step-up iframe → Cardinal → returnUrl (/3ds-complete.html) → TransactionId via postMessage
 *   5. validateAuthentication(TransactionId) → POST /risk/v1/authentication-results → cavv + eci
 *   6. processFinalPayment(paymentInfo, authData) → POST /pts/v2/payments with cavv/eci
 */
export class PortalApiService {

  /**
   * Step 1: Setup Payer Authentication
   */
  static async setupPayerAuth(paymentInfo: PaymentInfo): Promise<SetupPayerAuthResponse> {
    const response = await directApiInstance<SetupPayerAuthResponse>({
      url: PORTAL_API_URL,
      method: 'POST',
      data: {
        action: 'process_payment',
        use_3ds: true,
        payment_info: {
          merchant_reference: `INV_${Date.now()}`,
          amount: paymentInfo.amount,
          currency: paymentInfo.currency,
          card: {
            number: paymentInfo.cardNumber,
            expirationMonth: paymentInfo.expiryMonth,
            expirationYear: paymentInfo.expiryYear,
            securityCode: paymentInfo.cvv
          },
          payer_email: paymentInfo.email,
          payer_phone: paymentInfo.phone,
          billing_address: {
            firstName: paymentInfo.cardholderName.split(' ')[0],
            lastName: paymentInfo.cardholderName.split(' ').slice(1).join(' ') || 'Guest',
            email: paymentInfo.email,
            phoneNumber: paymentInfo.phone,
            country: 'DO', locality: paymentInfo.businessName || 'Punta Cana',
            postalCode: '23000', administrativeArea: 'La Altagracia', address1: 'Bavaro'
          },
          return_url: window.location.origin
        }
      }
    });
    return response as unknown as SetupPayerAuthResponse;
  }

  /**
   * Step 2: Collect Device Data (hidden iframe to Cardinal Commerce)
   */
  static collectDeviceData(deviceDataCollectionUrl: string, accessToken: string): Promise<boolean> {
    return new Promise((resolve) => {
      const iframe = document.createElement('iframe');
      iframe.name = 'cardinal_collection_iframe';
      iframe.id = 'cardinal_collection_iframe';
      iframe.style.display = 'none';
      iframe.width = '10';
      iframe.height = '10';
      document.body.appendChild(iframe);

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = deviceDataCollectionUrl;
      form.target = 'cardinal_collection_iframe';

      const tokenInput = document.createElement('input');
      tokenInput.type = 'hidden';
      tokenInput.name = 'JWT';
      tokenInput.value = accessToken;
      form.appendChild(tokenInput);
      document.body.appendChild(form);

      const cleanup = () => {
        if (document.body.contains(iframe)) document.body.removeChild(iframe);
        if (document.body.contains(form)) document.body.removeChild(form);
      };

      const messageHandler = (event: MessageEvent) => {
        try {
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          if (data.Status !== undefined) {
            window.removeEventListener('message', messageHandler);
            cleanup();
            resolve(true);
          }
        } catch { /* ignore */ }
      };

      window.addEventListener('message', messageHandler);
      form.submit(); // Submit AFTER listener is attached

      // 10 second timeout per Cybersource docs
      setTimeout(() => {
        window.removeEventListener('message', messageHandler);
        cleanup();
        resolve(true);
      }, 10000);
    });
  }

  /**
   * Step 3: Check Enrollment
   * CRITICAL: Must include returnUrl so Cardinal Commerce generates the step-up accessToken JWT.
   * Without returnUrl, accessToken is null and the step-up flow cannot proceed.
   */
  static async checkEnrollment(paymentInfo: PaymentInfo, referenceId: string): Promise<CheckEnrollmentResponse> {
    // The returnUrl is where Cardinal Commerce POSTs after the step-up challenge.
    // Cardinal ALWAYS POSTs (never GET), so the endpoint must be able to read POST body.
    //
    // On localhost: Vite's configureServer middleware intercepts POST to /3ds-complete.html
    //               and returns HTML with the TransactionId embedded.
    // In production: Static files on S3/CloudFront can't read POST bodies.
    //                We point to the Lambda API which has an early handler that catches
    //                form-urlencoded POST with TransactionId in body and returns the HTML.
    const returnUrl = resolveThreeDSReturnUrl();

    const response = await directApiInstance<CheckEnrollmentResponse>({
      url: PORTAL_API_URL,
      method: 'POST',
      data: {
        action: 'check_enrollment',
        clientReferenceInformation: {
          code: `INV_${Date.now()}`,
          comments: paymentInfo.businessName || undefined
        },
        orderInformation: {
          amountDetails: {
            totalAmount: paymentInfo.amount,
            currency: paymentInfo.currency
          },
          billTo: {
            firstName: paymentInfo.cardholderName.split(' ')[0],
            lastName: paymentInfo.cardholderName.split(' ').slice(1).join(' ') || 'Guest',
            email: paymentInfo.email,
            phoneNumber: paymentInfo.phone,
            country: 'DO', locality: paymentInfo.businessName || 'Punta Cana',
            postalCode: '23000', administrativeArea: 'La Altagracia', address1: 'Bavaro'
          }
        },
        paymentInformation: {
          card: {
            number: paymentInfo.cardNumber,
            expirationMonth: paymentInfo.expiryMonth,
            expirationYear: paymentInfo.expiryYear,
            securityCode: paymentInfo.cvv
          }
        },
        consumerAuthenticationInformation: {
          referenceId: referenceId,
          returnUrl: returnUrl,        // ← REQUIRED: Cardinal uses this to generate step-up JWT
          deviceChannel: 'BROWSER',
          transactionMode: 'eCommerce'
        },
        // Browser info fallback fields (increase frictionless auth rate)
        deviceInformation: {
          httpBrowserColorDepth: String(screen.colorDepth),
          httpBrowserJavaEnabled: false,
          httpBrowserJavaScriptEnabled: true,
          httpBrowserLanguage: navigator.language,
          httpBrowserScreenHeight: String(screen.height),
          httpBrowserScreenWidth: String(screen.width),
          httpBrowserTimeDifference: String(new Date().getTimezoneOffset()),
          userAgentBrowserValue: navigator.userAgent
        }
      }
    });

    return response as unknown as CheckEnrollmentResponse;
  }

  /**
   * Step 5: Validate Authentication Results
   * Called AFTER the step-up iframe completes and we receive TransactionId via postMessage.
   * The TransactionId from Cardinal (postMessage) is passed here, NOT the authenticationTransactionId.
   * Returns cavv, eciRaw, paresStatus, etc. needed for the final payment.
   */
  static async validateAuthentication(
    transactionId: string,
    paymentInfo: PaymentInfo
  ): Promise<ValidateAuthenticationResponse> {
    const response = await directApiInstance<ValidateAuthenticationResponse>({
      url: PORTAL_API_URL,
      method: 'POST',
      data: {
        action: 'validate_authentication',
        clientReferenceInformation: {
          code: `VAL_${Date.now()}`
        },
        orderInformation: {
          amountDetails: {
            totalAmount: paymentInfo.amount,
            currency: paymentInfo.currency
          }
        },
        paymentInformation: {
          card: {
            number: paymentInfo.cardNumber,
            expirationMonth: paymentInfo.expiryMonth,
            expirationYear: paymentInfo.expiryYear,
            securityCode: paymentInfo.cvv
          }
        },
        consumerAuthenticationInformation: {
          authenticationTransactionId: transactionId  // Cardinal TransactionId from postMessage
        }
      }
    });
    return response as unknown as ValidateAuthenticationResponse;
  }

  /**
   * Step 6: Process Final Payment
   * Includes cavv, eciRaw, paresStatus from validateAuthentication response.
   * commerceIndicator: 'vbv' (Visa), 'spa' (Mastercard), 'aesk' (Amex)
   */
  static async processFinalPayment(
    paymentInfo: PaymentInfo,
    authData: ThreeDSAuthData
  ): Promise<FinalPaymentResponse> {
    // Determine commerce indicator by card number prefix
    const cardPrefix = paymentInfo.cardNumber.replace(/\s/g, '')[0];
    const commerceIndicator = authData.commerceIndicator ||
      (cardPrefix === '4' ? 'vbv' : cardPrefix === '5' ? 'spa' : 'aesk');

    const response = await directApiInstance<FinalPaymentResponse>({
      url: PORTAL_API_URL,
      method: 'POST',
      data: {
        action: 'process_payment',
        clientReferenceInformation: {
          code: `PAY_${Date.now()}`,
          comments: paymentInfo.businessName || undefined
        },
        processingInformation: {
          capture: false,
          commerceIndicator  // 'vbv' for Visa, 'spa' for Mastercard, 'aesk' for Amex
        },
        orderInformation: {
          amountDetails: {
            totalAmount: paymentInfo.amount,
            currency: paymentInfo.currency
          },
          billTo: {
            firstName: paymentInfo.cardholderName.split(' ')[0],
            lastName: paymentInfo.cardholderName.split(' ').slice(1).join(' ') || 'Guest',
            email: paymentInfo.email,
            phoneNumber: paymentInfo.phone,
            country: 'DO', locality: paymentInfo.businessName || 'Punta Cana',
            postalCode: '23000', administrativeArea: 'La Altagracia', address1: 'Bavaro'
          }
        },
        paymentInformation: {
          card: {
            number: paymentInfo.cardNumber,
            expirationMonth: paymentInfo.expiryMonth,
            expirationYear: paymentInfo.expiryYear,
            securityCode: paymentInfo.cvv
          }
        },
        // Full auth data from step 5 validate response
        consumerAuthenticationInformation: {
          cavv: authData.cavv,
          eciRaw: authData.eciRaw,
          eci: authData.eci,
          paresStatus: authData.paresStatus,
          xid: authData.xid,
          ucafCollectionIndicator: authData.ucafCollectionIndicator,
          ucafAuthenticationData: authData.ucafAuthenticationData,
          directoryServerTransactionId: authData.directoryServerTransactionId,
          paSpecificationVersion: authData.paSpecificationVersion,
          acsTransactionId: authData.acsTransactionId,
          authenticationTransactionId: authData.authenticationTransactionId,
          ...(authData.strongAuthentication && {
            strongAuthentication: authData.strongAuthentication
          })
        },
        // Merchant-Defined Information — required by VisaNet DR for all payment requests
        merchantDefinedInformation: [
          { key: '1', value: paymentInfo.merchantCategory || 'RETAIL' },
          // key 2 (merchant ID) is set by the Lambda using ORGANIZATION_ID env var
          { key: '3', value: paymentInfo.channel || 'WEB' },
          { key: '4', value: paymentInfo.customerId || 'guest_user' },
          { key: '27', value: 'TOKENIZATION NO' },
          { key: '29', value: paymentInfo.idType || 'CEDULA' },
          { key: '30', value: paymentInfo.idNumber || '000-0000000-0' },
        ]
      }
    });
    return response as unknown as FinalPaymentResponse;
  }

  /** @deprecated Use setupPayerAuth() instead. */
  static async processPaymentWith3DS(paymentInfo: PaymentInfo): Promise<SetupPayerAuthResponse> {
    return PortalApiService.setupPayerAuth(paymentInfo);
  }
}

// ─── Booking-flow 3DS (SAM-199) ────────────────────────────────────────────────────────────────
//
// Same Cybersource/Cardinal 3DS chain as PortalApiService above (setup -> collect device data ->
// check enrollment -> [step-up] -> validate), reused for a front-desk SERVICE booking instead of
// forked: `collectDeviceData` and `ThreeDSModal` are gateway/flow-agnostic already and are reused
// completely unchanged. What's genuinely different is which Lambda route is called — the BOOKING
// lambda's own `/bookings/payment/{setup,enroll,validate}` (functions/booking/lambda_function.py)
// rather than `/portal` directly — because the final charge for a booking goes through
// `booking_orchestration.orchestrate_booking_payment`, which reuses `_authorize_portal_payment`
// (the exact same downstream CyberSource call payment_complete/this file's Step 6 already make),
// not `PortalApiService.processFinalPayment`. The booking lambda's response fields are its own
// snake_case shape (`reference_id`, `step_up_url`, `authentication_transaction_id`, ...) — kept as
// separate types rather than coerced into SetupPayerAuthResponse/CheckEnrollmentResponse/
// ValidateAuthenticationResponse above, which are a different shape end to end.

export interface BookingCardInput {
  number: string;
  expiration_month: string;
  expiration_year: string;
  security_code: string;
}

export interface BookingBillingAddress {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  address1?: string;
  locality?: string;
  administrative_area?: string;
  postal_code?: string;
  country?: string;
}

/** Step 1 (booking flow) response — POST /bookings/payment/setup */
export interface BookingPaymentSetupData {
  reference_id: string;
  device_data_collection_url: string;
  access_token: string;
  merchant_reference: string;
}

/** The snake_case auth-data shape `_authorize_portal_payment` (functions/booking/lambda_function.py)
 *  reads back off a subsequent orchestrate call — same fields payment_enroll/payment_validate hand
 *  back on the frictionless/challenge-complete path below. */
export interface BookingAuthData {
  cavv?: string;
  eci?: string;
  eci_raw?: string;
  pares_status?: string;
  xid?: string;
  indicator?: string;
  acs_transaction_id?: string;
  directory_server_transaction_id?: string;
  authentication_transaction_id?: string;
  strong_authentication?: Record<string, string>;
}

/** Step 3 (booking flow) response — POST /bookings/payment/enroll */
export interface BookingPaymentEnrollData {
  status: 'AUTHENTICATION_SUCCESSFUL' | 'PENDING_AUTHENTICATION';
  auth_data?: BookingAuthData; // present only for the frictionless (AUTHENTICATION_SUCCESSFUL) path
  step_up_url?: string;
  access_token?: string;
  authentication_transaction_id?: string;
}

/** Step 5 (booking flow) response — POST /bookings/payment/validate */
export interface BookingPaymentValidateData {
  status: string; // "AUTHENTICATION_SUCCESSFUL" | anything else = failed
  auth_data: BookingAuthData;
}

export class BookingPaymentService {
  /** Step 1: Setup Payer Authentication for a booking charge. */
  static async setupPayerAuth(params: {
    serviceId: string;
    amount: number;
    currency: string;
    card: BookingCardInput;
    billingAddress: BookingBillingAddress;
    merchantName?: string;
  }): Promise<BookingPaymentSetupData> {
    const response = await directApiInstance<{ data?: BookingPaymentSetupData; message?: string }>({
      url: `${BOOKING_API_URL}/payment/setup`,
      method: 'POST',
      data: {
        service_id: params.serviceId,
        amount: params.amount,
        currency: params.currency,
        card: params.card,
        billing_address: params.billingAddress,
        merchant_name: params.merchantName,
        return_url: window.location.origin,
      },
    });
    const data = (response as any)?.data;
    if (!data?.device_data_collection_url || !data?.access_token || !data?.reference_id) {
      throw new Error((response as any)?.message || 'Failed to initialize booking payment authentication');
    }
    return data;
  }

  /** Step 2: Collect Device Data — identical mechanics to the product flow, reused as-is. */
  static collectDeviceData(deviceDataCollectionUrl: string, accessToken: string): Promise<boolean> {
    return PortalApiService.collectDeviceData(deviceDataCollectionUrl, accessToken);
  }

  /**
   * Step 3: Check Enrollment. Uses the SAME returnUrl the product flow uses (see
   * `resolveThreeDSReturnUrl`'s docstring) so `ThreeDSModal`'s existing detection keeps working
   * unmodified for a booking charge.
   */
  static async checkEnrollment(params: {
    serviceId: string;
    referenceId: string;
    amount: number;
    currency: string;
    card: BookingCardInput;
    billingAddress: BookingBillingAddress;
    merchantName?: string;
    merchantReference?: string;
  }): Promise<BookingPaymentEnrollData> {
    const response = await directApiInstance<{ data?: BookingPaymentEnrollData; message?: string }>({
      url: `${BOOKING_API_URL}/payment/enroll`,
      method: 'POST',
      data: {
        service_id: params.serviceId,
        reference_id: params.referenceId,
        amount: params.amount,
        currency: params.currency,
        card: params.card,
        billing_address: params.billingAddress,
        merchant_name: params.merchantName,
        merchant_reference: params.merchantReference,
        return_url: resolveThreeDSReturnUrl(),
      },
    });
    const data = (response as any)?.data;
    if (!data?.status) {
      throw new Error((response as any)?.message || 'Failed to check 3DS enrollment');
    }
    return data;
  }

  /**
   * Step 5: Validate Authentication Results, called after the ThreeDSModal step-up completes
   * (same `transactionId` postMessage/same-origin-iframe mechanism as the product flow).
   */
  static async validateAuthentication(params: {
    serviceId: string;
    transactionId: string;
    amount: number;
    currency: string;
    card: BookingCardInput;
    merchantReference?: string;
  }): Promise<BookingPaymentValidateData> {
    const response = await directApiInstance<{ data?: BookingPaymentValidateData; message?: string }>({
      url: `${BOOKING_API_URL}/payment/validate`,
      method: 'POST',
      data: {
        service_id: params.serviceId,
        authentication_transaction_id: params.transactionId,
        amount: params.amount,
        currency: params.currency,
        card: params.card,
        merchant_reference: params.merchantReference,
      },
    });
    const data = (response as any)?.data;
    if (!data || data.status !== 'AUTHENTICATION_SUCCESSFUL') {
      throw new Error((response as any)?.message || `3DS validation failed: ${data?.status || 'unknown'}`);
    }
    return data;
  }
}
